import { Component, OnDestroy, OnInit } from '@angular/core';
import { faAddressCard, faEdit, faHistory, faUserSlash, faUsers } from '@fortawesome/pro-duotone-svg-icons';
import { faEnvelope, faPhone, faStar } from '@fortawesome/pro-solid-svg-icons';
import { Select, Store } from '@ngxs/store';
import _ from 'lodash';
import { Observable, Subject, combineLatest, interval } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, share, takeUntil, tap } from 'rxjs/operators';
import { WellKnownIconsEnum } from 'src/app/helper/iconHelper';
import { UserStatusEnum } from 'src/app/samwebDescription/samwin/UserStatusEnum';
import {
   ChangeSkillStatusOfSelectedAgent,
   ToggleAgentDetailsEditMode,
} from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/agent-details-behavior/agent-details-behavior.actions';
import { AgentDetailsBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/agent-details-behavior/agent-details-behavior.state';
import { BargeInConference, BargeInListen, BargeInWhisper } from 'src/app/store/samwin/client-phone/client-phone.actions';
import { ReloadCallHistoryForUser } from 'src/app/store/samwin/samwin-call-history/samwin-call-history.action';
import { RegisterForForeignSkillInfos } from 'src/app/store/samwin/samwin-skills/samwin-skills.actions';
import { SkillGroup } from 'src/app/store/samwin/samwin-skills/skill-group';
import { SamwinUserAssistanceState } from 'src/app/store/samwin/samwin-user-assistance-status/samwin-user-assistance-status.state';
import { EnabledFeaturesState } from 'src/app/store/session/enabled-features/enabled-features.state';
import { AgentListEntryRemoteDto } from 'src/generated/signalR/agent-list-entry-remote-dto';
import { CallHistoryEntryRemoteDto } from 'src/generated/swagger';
import { BrickCategory } from '../../../../samwebDescription/samweb/brickCategoryType';
import { AgentDetailsSettingState } from '../../../../store/dashboard/brick-wall/brick-settings/specific-brick-settings/agent-details-brick-settings/agent-details-settings.state';
import { Skill } from '../../../../store/samwin/samwin-skills/skill';
import { BasicBrickComponent } from '../../basic-brick-component';
import { SamwebBrick } from '../../samweb-brick.decorator';
import { AgentDetailsSettingsComponent } from './agent-details-settings/agent-details-settings.component';

@Component({
   selector: 'samweb-agent-details-brick',
   templateUrl: './agent-details-brick.component.html',
   styleUrls: ['./agent-details-brick.component.scss'],
})
@SamwebBrick({
   brickDefinitionId: 'agentDetails',
   brickTitleTranslationKey: 'brick.brick_definitions.agent_details.title',
   brickDescriptionTranslationKey: 'brick.brick_definitions.agent_details.description',
   brickIcon: faAddressCard,
   minColWidth: 10,
   minRowHeight: 10,
   brickCategory: BrickCategory.GENERAL,
   settingsComponent: AgentDetailsSettingsComponent,
   settingStateHandler: AgentDetailsSettingState,
})
export class AgentDetailsBrickComponent extends BasicBrickComponent implements OnInit, OnDestroy {
   private static callHistoryUpdateInterval$ = interval(5000).pipe(share());

   public callerIcon = faPhone;
   public skillIcon = faStar;
   public groupIcon = faUsers;
   public callHistoryIcon = faHistory;
   public noAgentSelectedIcon = faUserSlash;
   public listenIcon = WellKnownIconsEnum.BARGE_IN_LISTEN.icon;
   public whisperIcon = WellKnownIconsEnum.BARGE_IN_WHISPER.icon;
   public conferenceIcon = WellKnownIconsEnum.BARGE_IN_CONFERENCE.icon;
   public editIcon = faEdit;
   public mailingIcon = faEnvelope;
   public callingIcon = faPhone;
   public OFFLINE = UserStatusEnum.OFFLINE.value;

   @Select(AgentDetailsBehaviourState.getAgentDetailsForSelectedAgentId)
   public currentUser$: Observable<AgentListEntryRemoteDto>;
   public currentUserDebounced$ = new Subject<AgentListEntryRemoteDto>();

   @Select(AgentDetailsBehaviourState.getSelectedAgentIsOnline)
   public isAgentOnline$: Observable<boolean>;

   private destroy$ = new Subject();

   public showCaller$: Observable<boolean>;

   public showSkills$: Observable<boolean>;

   public showGroups$: Observable<boolean>;

   public showCallHistory$: Observable<boolean>;

   public showAvatar$: Observable<boolean>;

   @Select(AgentDetailsBehaviourState.isInEditMode)
   public isInEditMode$: Observable<boolean>;

   @Select(EnabledFeaturesState.isSupervisor)
   public isSupervisor$: Observable<boolean>;

   @Select(SamwinUserAssistanceState.canBargeInToSelectedAgent)
   public canBargeIn$: Observable<boolean>;

   @Select(SamwinUserAssistanceState.isListeningToSelectedAgent)
   public isListeningToSelectedAgent$: Observable<boolean>;

   @Select(SamwinUserAssistanceState.isWhisperingToSelectedAgent)
   public isWhisperingToSelectedAgent$: Observable<boolean>;

   @Select(SamwinUserAssistanceState.isConferencingWithSelectedAgent)
   public isConferencingWithSelectedAgent$: Observable<boolean>;

   @Select(AgentDetailsBehaviourState.getSkillGroupsForSelectedAgentId)
   public skillList$: Observable<SkillGroup[]>;

   @Select(AgentDetailsBehaviourState.skillIdsOfSelectedAgent)
   public skillIds$: Observable<number[]>;

   @Select(AgentDetailsBehaviourState.getSelectedAgentIsLoggedOn)
   public isAgentLoggedOn$: Observable<boolean>;

   public callHistory$: Observable<CallHistoryEntryRemoteDto[]>;

   constructor(private store: Store) {
      super();
   }

   ngOnInit(): void {
      this.showCaller$ = this.store.select(AgentDetailsSettingState.getShowCaller).pipe(map((fn) => fn(this.brickInstanceId)));
      this.showSkills$ = this.store.select(AgentDetailsSettingState.getShowSkills).pipe(map((fn) => fn(this.brickInstanceId)));
      this.showGroups$ = this.store.select(AgentDetailsSettingState.getShowGroups).pipe(map((fn) => fn(this.brickInstanceId)));
      this.showCallHistory$ = this.store.select(AgentDetailsSettingState.getShowCallHistory).pipe(map((fn) => fn(this.brickInstanceId)));
      this.showAvatar$ = this.store.select(AgentDetailsSettingState.getShowAvatar).pipe(map((fn) => fn(this.brickInstanceId)));
      this.callHistory$ = this.store.select(AgentDetailsBehaviourState.getCallHistoryForSelectedAgent).pipe(map((x) => x(this.brickInstanceId)));

      const maxNumOfEntriesInCallHistory$ = this.store.select(AgentDetailsSettingState.getMaxNumOfEntriesInCallHistory);
      combineLatest([this.currentUser$, maxNumOfEntriesInCallHistory$, AgentDetailsBrickComponent.callHistoryUpdateInterval$])
         .pipe(takeUntil(this.destroy$))
         .subscribe(([currentUser, maxNumOfEntriesInCallHistory]) => {
            if (_.isNil(currentUser)) {
               return; // nothing to do
            }

            this.store.dispatch(new ReloadCallHistoryForUser(currentUser.userId, maxNumOfEntriesInCallHistory));
         });

      const noUser = new AgentListEntryRemoteDto();
      noUser.userId = 0;

      this.currentUser$
         .pipe(
            tap(() => this.currentUserDebounced$.next(noUser)),
            debounceTime(300),
            tap((currentUser) => this.currentUserDebounced$.next(currentUser)),
            takeUntil(this.destroy$)
         )
         .subscribe();

      combineLatest([this.currentUserDebounced$, this.skillIds$])
         .pipe(
            distinctUntilChanged(),
            filter(([user, skills]) => user !== noUser && !_.isEmpty(skills)),
            takeUntil(this.destroy$)
         )
         .subscribe(([user, skillIds]) => {
            this.store.dispatch(new RegisterForForeignSkillInfos(skillIds));
         });
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   public bargeInListen() {
      this.store.dispatch(new BargeInListen());
   }

   public bargeInWhisper() {
      this.store.dispatch(new BargeInWhisper());
   }

   public bargeInConference() {
      this.store.dispatch(new BargeInConference());
   }

   public toggleEditMode() {
      this.store.dispatch(new ToggleAgentDetailsEditMode());
   }

   changeSkillStatus(skills: Skill[], $event: any) {
      skills.forEach((skill) => {
         if (skill.skillUserConfiguration.isManageable) {
            this.store.dispatch(new ChangeSkillStatusOfSelectedAgent(skill.skillId, $event.currentTarget.checked));
         }
      });
      return false;
   }
}
